import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { CAPABILITY_LABEL, CAPABILITY_SHORT, coverageGaps, type AiCapability, type AiProviderDef, type AiProviderId, type AiSettings, type LanguageSupport } from "@gk/shared";
import { api } from "../../lib/api";
import { fmtDate, useAsync } from "../../lib/hooks";
import { ErrorNote, Modal } from "../../components/ui";
import { AdminTabs, AiChoiceEditor, ServeGrid, VendorChip, type ProviderStatusMap } from "./common";

interface AiView {
  providers: AiProviderDef[];
  languages: Array<{ code: string; name: string }>;
  settings: AiSettings;
  status: ProviderStatusMap;
  support: Record<string, Record<string, LanguageSupport | null>>;
  credentials: Array<{ provider: string; public: Record<string, string>; keysSet: string[]; updatedAt: string; updatedBy: string | null }>;
  overrides: Array<{ id: string; slug: string; name: string; capabilities: string[] }>;
}

interface TestResult {
  ok: boolean;
  latencyMs: number;
  details: string[];
  error?: string;
}

interface Price {
  provider: AiProviderId;
  capability: AiCapability;
  model: string;
  unit: "minute" | "million_chars" | "million_tokens";
  usd: number;
  usdOutput: number | null;
  note: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

const UNIT_LABEL = { minute: "per minute", million_chars: "per million characters", million_tokens: "per million tokens (in / out)" };

export function AdminAiPage() {
  const view = useAsync(() => api.get<AiView>("/api/admin/ai"), []);
  const [draft, setDraft] = useState<AiSettings | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [saved, setSaved] = useState(false);
  const [keysFor, setKeysFor] = useState<AiProviderDef | null>(null);

  useEffect(() => {
    if (view.data && !draft) setDraft(view.data.settings);
  }, [view.data, draft]);

  const changed = useMemo(() => Boolean(view.data && draft && JSON.stringify(view.data.settings) !== JSON.stringify(draft)), [view.data, draft]);
  const gaps = draft ? coverageGaps(draft) : [];

  if (view.error) return <ErrorNote error={view.error} />;
  if (!view.data || !draft) return <p className="muted">Loading…</p>;
  const v = view.data;
  const langName = (c: string) => v.languages.find((l) => l.code === c)?.name ?? c;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put("/api/admin/ai/settings", draft);
      setConfirm(false);
      setSaved(true);
      setDraft(null);
      await view.reload();
    } catch (err) {
      setError(err as Error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Admin panel</h1>
          <p className="sub">Choose which AI services the platform uses. A change applies from each client's next interview; interviews in progress keep their providers.</p>
        </div>
      </div>
      <AdminTabs />
      <div className="stack" style={{ gap: 20 }}>
        <section className="panel stack" aria-labelledby="default-h">
          <div className="panel-head">
            <h3 id="default-h">Platform default</h3>
            <p>Every client uses these unless it has its own choice.</p>
          </div>
          <AiChoiceEditor value={draft} onChange={(s) => (setDraft(s), setSaved(false))} providers={v.providers} status={v.status} />
          <div className="panel-head" style={{ marginTop: 8, marginBottom: 0 }}>
            <h3>Who serves each language</h3>
            <p>Updates as you choose. A gap means interviews in that language lose that service.</p>
          </div>
          <ServeGrid settings={draft} languages={v.languages} />
          <ErrorNote error={error} />
          <div className="row">
            {saved && !changed && <span className="pill green">Saved</span>}
            {gaps.length > 0 && <span className="pill amber">{gaps.length} language gap{gaps.length === 1 ? "" : "s"}</span>}
            <span className="spacer" />
            <button className="btn ghost" disabled={!changed} onClick={() => setDraft(v.settings)}>
              Discard changes
            </button>
            <button className="btn primary" disabled={!changed} onClick={() => setConfirm(true)}>
              Review and save
            </button>
          </div>
        </section>

        {v.overrides.length > 0 && (
          <section className="panel">
            <div className="panel-head">
              <h3>Clients with their own choice</h3>
              <p>These do not follow the default for the services listed.</p>
            </div>
            <ul className="plain-list">
              {v.overrides.map((o) => (
                <li key={o.id}>
                  <Link to={`/admin/clients/${o.id}`}>{o.name}</Link>{" "}
                  <span className="muted small">— {o.capabilities.map((c) => CAPABILITY_LABEL[c as AiCapability]).join(", ")}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="stack" aria-labelledby="providers-h" style={{ gap: 12 }}>
          <h3 id="providers-h">Providers</h3>
          <div className="provider-cards">
            {v.providers.map((p) => (
              <ProviderCard key={p.id} def={p} status={v.status[p.id] ?? {}} cred={v.credentials.find((c) => c.provider === p.id)} draft={draft} onKeys={() => setKeysFor(p)} support={v.support[p.id] ?? {}} langName={langName} />
            ))}
          </div>
        </section>

        <Prices />
      </div>

      {confirm && (
        <Modal title="Save the platform default?" onClose={() => setConfirm(false)}>
          <div className="stack">
            <ChangeSummary before={v.settings} after={draft} />
            {gaps.length > 0 && (
              <div className="note-box">
                Not covered: {gaps.map((g) => `${CAPABILITY_LABEL[g.capability]} in ${langName(g.language)}`).join("; ")}. Add a fallback to close the gap, or save anyway.
              </div>
            )}
            <p className="small muted">
              Clients without their own choice switch on their next interview. Spoken questions and glossaries are rebuilt for the new providers in the background.
              Make sure each new provider is listed in your client agreements as a sub-processor.
            </p>
            <ErrorNote error={error} />
            <div className="row">
              <span className="spacer" />
              <button className="btn ghost" onClick={() => setConfirm(false)}>
                Cancel
              </button>
              <button className="btn primary" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save default"}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {keysFor && (
        <KeysModal
          def={keysFor}
          cred={v.credentials.find((c) => c.provider === keysFor.id)}
          onClose={() => setKeysFor(null)}
          onSaved={async () => {
            setKeysFor(null);
            await view.reload();
          }}
        />
      )}
    </>
  );
}

function ChangeSummary({ before, after }: { before: AiSettings; after: AiSettings }) {
  const rows: Array<[string, string, string]> = [];
  for (const c of ["stt", "translate", "tts"] as const) {
    const b = `${before[c].provider}${before[c].fallback ? ` + ${before[c].fallback}` : ""}`;
    const a = `${after[c].provider}${after[c].fallback ? ` + ${after[c].fallback}` : ""}`;
    if (a !== b) rows.push([CAPABILITY_LABEL[c], b, a]);
  }
  const lb = `${before.llm.provider}: ${before.llm.analysisModel} / ${before.llm.liveModel}`;
  const la = `${after.llm.provider}: ${after.llm.analysisModel} / ${after.llm.liveModel}`;
  if (la !== lb) rows.push([CAPABILITY_LABEL.llm, lb, la]);
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Service</th>
          <th>Now</th>
          <th>After saving</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([c, b, a]) => (
          <tr key={c}>
            <td>{c}</td>
            <td className="muted">{b}</td>
            <td>
              <b>{a}</b>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProviderCard({
  def,
  status,
  cred,
  draft,
  onKeys,
  support,
  langName,
}: {
  def: AiProviderDef;
  status: Record<string, { ok: boolean; reason?: string }>;
  cred?: AiView["credentials"][number];
  draft: AiSettings;
  onKeys: () => void;
  support: Record<string, LanguageSupport | null>;
  langName: (c: string) => string;
}) {
  const [results, setResults] = useState<Record<string, TestResult | "running">>({});
  const run = async (capability: AiCapability) => {
    setResults((r) => ({ ...r, [capability]: "running" }));
    try {
      const body = capability === "llm" && draft.llm.provider === def.id ? { provider: def.id, capability, analysisModel: draft.llm.analysisModel, liveModel: draft.llm.liveModel } : { provider: def.id, capability };
      const res = await api.post<TestResult>("/api/admin/ai/test", body);
      setResults((r) => ({ ...r, [capability]: res }));
    } catch (err) {
      setResults((r) => ({ ...r, [capability]: { ok: false, latencyMs: 0, details: [], error: (err as Error).message } }));
    }
  };
  const unsupported = Object.entries(support)
    .flatMap(([lang, s]) => (s ? (["stt", "tts", "translate"] as const).filter((c) => def.capabilities.includes(c) && s[c].status === "unsupported").map((c) => `${CAPABILITY_LABEL[c]}: ${langName(lang)}`) : []))
    .slice(0, 6);
  const notes = Object.entries(support)
    .flatMap(([lang, s]) => (s ? [s.stt.note, s.tts.note].filter(Boolean).map((n) => `${langName(lang)} — ${n}`) : []))
    .slice(0, 4);

  return (
    <article className="panel provider-card" aria-labelledby={`p-${def.id}`}>
      <div className="row">
        <VendorChip id={def.id} />
        <h4 id={`p-${def.id}`} style={{ margin: 0 }}>
          {def.name}
        </h4>
        <span className="spacer" />
        {def.credentials.length > 0 && (
          <button className="btn small" onClick={onKeys}>
            {cred ? "Update keys" : "Set up keys"}
          </button>
        )}
      </div>
      <p className="small muted">{def.note}</p>
      {cred && (
        <p className="xs muted">
          Keys saved {fmtDate(cred.updatedAt)}
          {cred.updatedBy ? ` by ${cred.updatedBy}` : ""}. {Object.entries(cred.public).map(([k, val]) => `${k}: ${val}`).join(" · ")}
        </p>
      )}
      <table className="table compact">
        <tbody>
          {def.capabilities.map((c) => {
            const st = status[c];
            const r = results[c];
            return (
              <tr key={c}>
                <td>{CAPABILITY_SHORT[c]}</td>
                <td>{st?.ok ? <span className="pill green">Ready</span> : <span className="pill">{st?.reason ?? "Not set up"}</span>}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn small ghost" disabled={!st?.ok || r === "running"} onClick={() => run(c)}>
                    {r === "running" ? "Testing…" : "Test"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {Object.entries(results).map(([c, r]) =>
        r === "running" ? null : (
          <div key={c} className={r.ok ? "test-ok" : "error-box"} role="status">
            <b>
              {CAPABILITY_SHORT[c as AiCapability]}: {r.ok ? `worked in ${(r.latencyMs / 1000).toFixed(1)} s` : "failed"}
            </b>
            {r.error && <div>{r.error}</div>}
            {r.details.map((d, i) => (
              <div key={i} className="small">
                {d}
              </div>
            ))}
          </div>
        ),
      )}
      {(unsupported.length > 0 || notes.length > 0) && (
        <details className="small">
          <summary>Language notes</summary>
          <ul>
            {unsupported.map((u) => (
              <li key={u}>Not offered — {u}</li>
            ))}
            {notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </details>
      )}
      <p className="xs faint">Processes data as: {def.processor}</p>
    </article>
  );
}

function KeysModal({ def, cred, onClose, onSaved }: { def: AiProviderDef; cred?: AiView["credentials"][number]; onClose: () => void; onSaved: () => void }) {
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(def.credentials.filter((f) => !f.secret).map((f) => [f.key, cred?.public[f.key] ?? ""])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/admin/ai/credentials/${def.id}`, values);
      onSaved();
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Remove the saved keys for ${def.name}?`)) return;
    setBusy(true);
    try {
      await api.del(`/api/admin/ai/credentials/${def.id}`);
      onSaved();
    } catch (err) {
      setError(err as Error);
      setBusy(false);
    }
  };
  return (
    <Modal title={`${def.name} keys`} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <p className="small muted">Keys are stored in the secret store and never shown again. Leave a key blank to keep the saved one. Use a key limited to these services.</p>
        {def.credentials.map((f) => (
          <label className="field" key={f.key}>
            <span>
              {f.label}
              {f.optional ? <span className="faint"> (optional)</span> : null}
            </span>
            <input
              className="input"
              type={f.secret ? "password" : "text"}
              autoComplete="off"
              value={values[f.key] ?? ""}
              placeholder={f.secret && cred?.keysSet.includes(f.key) ? "Saved — leave blank to keep" : (f.placeholder ?? "")}
              onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
            />
            {f.help && <span className="xs muted">{f.help}</span>}
          </label>
        ))}
        <ErrorNote error={error} />
        <div className="row">
          {cred && (
            <button type="button" className="btn ghost danger" onClick={remove} disabled={busy}>
              Remove keys
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy}>
            {busy ? "Saving…" : "Save keys"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Prices() {
  const list = useAsync(() => api.get<Price[]>("/api/admin/ai/prices"), []);
  const [edit, setEdit] = useState<Price | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!edit) return;
    setError(null);
    try {
      await api.put("/api/admin/ai/prices", { ...edit, usd: Number(edit.usd), usdOutput: edit.usdOutput == null || (edit.usdOutput as unknown) === "" ? null : Number(edit.usdOutput) });
      setEdit(null);
      await list.reload();
    } catch (err) {
      setError(err as Error);
    }
  };
  return (
    <details className="panel flush prices">
      <summary className="panel-head" style={{ padding: "16px 20px" }}>
        <h3>Prices used for spend estimates</h3>
        <p>List prices in US dollars. Check them against your contracts; estimates are not invoices.</p>
      </summary>
      <table className="table">
        <thead>
          <tr>
            <th>Provider</th>
            <th>Service</th>
            <th>Model</th>
            <th>Price</th>
            <th>Note</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(list.data ?? []).map((p) => (
            <tr key={`${p.provider}-${p.capability}-${p.model}`}>
              <td>
                <VendorChip id={p.provider} />
              </td>
              <td>{CAPABILITY_SHORT[p.capability]}</td>
              <td className="small">{p.model === "*" ? "All" : p.model}</td>
              <td className="small">
                ${p.usd}
                {p.usdOutput != null ? ` / $${p.usdOutput}` : ""} <span className="muted">{UNIT_LABEL[p.unit]}</span>
              </td>
              <td className="xs muted">{p.note}</td>
              <td>
                <button className="btn small ghost" onClick={() => setEdit(p)}>
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {edit && (
        <Modal title="Edit price" onClose={() => setEdit(null)}>
          <form className="stack" onSubmit={save}>
            <p className="small">
              {edit.provider} · {CAPABILITY_LABEL[edit.capability]} · {edit.model === "*" ? "all models" : edit.model} · {UNIT_LABEL[edit.unit]}
            </p>
            <div className="grid2">
              <label className="field">
                <span>{edit.unit === "million_tokens" ? "Input (USD)" : "Price (USD)"}</span>
                <input className="input" type="number" step="0.000001" min="0" value={edit.usd} onChange={(e) => setEdit({ ...edit, usd: e.target.value as unknown as number })} required />
              </label>
              {edit.unit === "million_tokens" && (
                <label className="field">
                  <span>Output (USD)</span>
                  <input className="input" type="number" step="0.000001" min="0" value={edit.usdOutput ?? ""} onChange={(e) => setEdit({ ...edit, usdOutput: e.target.value as unknown as number })} />
                </label>
              )}
            </div>
            <label className="field">
              <span>Note</span>
              <input className="input" value={edit.note ?? ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} maxLength={300} />
            </label>
            <ErrorNote error={error} />
            <div className="row">
              <span className="spacer" />
              <button type="button" className="btn ghost" onClick={() => setEdit(null)}>
                Cancel
              </button>
              <button className="btn primary">Save price</button>
            </div>
          </form>
        </Modal>
      )}
    </details>
  );
}
